import { Dimensions, Size } from 'types';
import { Pixels, ViewportHeight, ViewportWidth } from 'utils/measurement';
import {
  fitElement,
  measurementToPx,
  measurementToViewport,
} from 'utils/placement';
import { VideoTemplateStateContent } from '../types';

/**
 * Returns true when canvasPx is fully covered by containerPx — i.e. the
 * container spans or exceeds the canvas on every side.
 */
const checkIsContainerCoveringCanvas = (
  containerPx: Dimensions<Pixels>,
  canvasPx: Size<Pixels>,
): boolean =>
  containerPx.left.value <= 0 &&
  containerPx.top.value <= 0 &&
  containerPx.left.value + containerPx.width.value >= canvasPx.width.value &&
  containerPx.top.value + containerPx.height.value >= canvasPx.height.value;

/**
 * Returns the intersection of containerPx with the canvas, i.e. the portion of
 * the container that is actually on-canvas. Width/height are clamped to zero
 * when the container lies entirely outside the canvas.
 */
const clipToCanvas = (
  containerPx: Dimensions<Pixels>,
  canvasPx: Size<Pixels>,
): Dimensions<Pixels> => {
  const left = Math.max(containerPx.left.value, 0);
  const top = Math.max(containerPx.top.value, 0);
  const right = Math.min(
    containerPx.left.value + containerPx.width.value,
    canvasPx.width.value,
  );
  const bottom = Math.min(
    containerPx.top.value + containerPx.height.value,
    canvasPx.height.value,
  );

  return {
    height: new Pixels(Math.max(bottom - top, 0)),
    left: new Pixels(left),
    top: new Pixels(top),
    width: new Pixels(Math.max(right - left, 0)),
  };
};

/**
 * Allows resizing an asset to fit its layers dimensions when replacing it.
 * It fits the new asset's dimensions at the current layer and re-adjusts the
 * new asset dimensions in order not to lose the aspect ratio.
 * @param {object} state - current editor state
 * @param {object} currPlacement - previous asset placement
 * @param {object} [assetSize] - new asset size.
 * @returns {object} - a new dimensions object with the placement and size for the newly added asset.
 */
export const getUpdatedPlacement = (
  state: VideoTemplateStateContent,
  currPlacement: Dimensions<
    ViewportHeight,
    ViewportWidth,
    ViewportHeight,
    ViewportWidth
  >,
  assetSize?: { height: number; width: number },
) => {
  // if asset size is not available (corner case), new placement ignores height and width
  if (!assetSize) {
    return {
      height: undefined,
      width: undefined,
      left: currPlacement.left,
      top: currPlacement.top,
    };
  }

  const { canvas } = state;

  // canvas dimensions are transformed to px, then the same is done with new asset
  // dimensions. Finally new dimensions are fit to canvas container and those dimensions
  // are measured with viewport units.
  const canvasPx = {
    height: new Pixels(canvas.height),
    width: new Pixels(canvas.width),
  };

  const containerPx = measurementToPx(currPlacement, canvasPx);

  const assetDimensions: Size<Pixels> = {
    height: new Pixels(assetSize.height),
    width: new Pixels(assetSize.width),
  };

  // Clip the placeholder to the canvas so the replacement always lands
  // on-canvas, even when the template's container extends past one or more
  // edges (a stand-in sized for a different source aspect ratio, e.g. a
  // landscape placeholder on a 9:16 canvas). Clipping can change the
  // container's aspect ratio.
  const clippedContainerPx = clipToCanvas(containerPx, canvasPx);

  // A placeholder that spans the entire canvas is a full-bleed stand-in: cover
  // the canvas with the new asset (centered crop). A genuine sub-region keeps
  // the template's letterbox fit, anchored at its top-left corner.
  if (checkIsContainerCoveringCanvas(containerPx, canvasPx)) {
    const filledSize = fitElement(assetDimensions, clippedContainerPx, 'fill');

    return measurementToViewport(
      {
        height: filledSize.height,
        left: clippedContainerPx.left.plus(filledSize.left),
        top: clippedContainerPx.top.plus(filledSize.top),
        width: filledSize.width,
      },
      canvas,
    );
  }

  const fittedSize = fitElement(assetDimensions, clippedContainerPx, 'fit');

  return measurementToViewport(
    {
      height: fittedSize.height,
      left: clippedContainerPx.left,
      top: clippedContainerPx.top,
      width: fittedSize.width,
    },
    canvas,
  );
};
